import { FormControl, FormField, FormItem, FormLabel } from 'components/ui/form';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { Textarea } from 'components/ui/textarea';
import { Button } from 'components/ui/button';
import { Switch } from 'components/ui/switch';
import type { ChatbotWidget } from './types';
import { CloudUpload, Trash } from 'lucide-react';
import { useState, useRef } from 'react';
import { useConfig } from 'hooks/use-config';
import { useToast } from 'hooks/use-toast';
import { useGetPreSignedUrlForUpload } from 'lib/api/hooks/use-storage';
import { cn } from 'lib/utils';
import { ModuleName } from 'constant/modules.constants.ts';

const sectionTitleClass =
  'text-[18px] font-semibold leading-[1.5] text-[#3d3d3d] dark:text-foreground';

const fieldLabelClass =
  'text-[16px] font-semibold leading-[1.2] text-[#3d3d3d] dark:text-foreground';

const textareaClass =
  'min-h-[56px] resize-none rounded-lg border-[#e7e7e7] bg-white px-5 py-4 text-[16px] font-normal leading-[1.6] text-[#3d3d3d] shadow-none focus-visible:ring-0 dark:border-border dark:bg-card dark:text-foreground';

const switchClass =
  'h-5 w-9 border-transparent bg-[#e7e7e7] data-[state=checked]:bg-black dark:data-[state=checked]:bg-foreground [&_[data-state]]:size-4 [&_[data-state=checked]]:translate-x-[16px] [&_[data-state=unchecked]]:translate-x-0.5';

export const AgentIntegrationGlobalSettings = () => {
  const { t } = useTranslation();
  const form = useFormContext<ChatbotWidget>();
  const { isValid } = form.formState;
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const { toast } = useToast();
  const presignedUploadMutation = useGetPreSignedUrlForUpload();
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (file: File) => {
    try {
      setIsUploading(true);

      if (file.size > 1 * 1024 * 1024) {
        toast({
          title: t('ERROR'),
          description: t('IMAGE_MAX_SIZE_1MB'),
          variant: 'destructive',
        });
        return;
      }

      const payload = {
        name: file.name,
        projectKey: blocksKey,
        configurationName: 'Default',
        accessModifier: 'Public' as const,
        metaData: '',
        parentDirectoryId: '',
        tags: '',
        moduleName: ModuleName.AICloud,
      };

      const preSignedUrlRes = await presignedUploadMutation.mutateAsync(payload);

      if (!preSignedUrlRes.isSuccess || !preSignedUrlRes.uploadUrl || !preSignedUrlRes.fileId) {
        throw new Error('Failed to get upload URL');
      }

      await fetch(preSignedUrlRes.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
          'x-ms-blob-type': 'Blockblob',
        },
      });

      const imageUrl = preSignedUrlRes.uploadUrl.split('?')[0];
      form.setValue('designSettings.banner.imageUrl', imageUrl);
      form.setValue('designSettings.banner.imageId', preSignedUrlRes.fileId);

      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('IMAGE_UPLOADED_SUCCESSFULLY'),
      });
    } catch {
      toast({
        title: t('ERROR'),
        description: t('FAILED_TO_UPLOAD_IMAGE'),
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleFileUpload(file);
    }
  };

  return (
    <div className="flex w-full flex-col gap-8 pb-4">
      {/* Note — Figma 3042:36170 */}
      <div className="flex gap-1.5 border-b border-[#e7e7e7] pb-6 text-[14px] dark:border-border">
        <span className="shrink-0 font-semibold leading-[1.2] text-[#3d3d3d] dark:text-foreground">
          *{t('NOTE')}:
        </span>
        <span className="min-w-0 flex-1 font-normal leading-[1.6] text-[#6f6b7d] dark:text-muted-foreground">
          {t('GLOBAL_SETTING_AGENT_NOTE')}
        </span>
      </div>

      <div className="flex w-full flex-col gap-7">
        {/* Banner — Figma 3042:36174 */}
        <div className="flex w-full flex-col gap-8 border-b border-[#e7e7e7] pb-8 dark:border-border">
          <h3 className={sectionTitleClass}>{t('BANNER')}</h3>

          <div className="flex w-full flex-col gap-6">
            <FormField
              control={form.control}
              name="designSettings.banner.enabled"
              render={({ field }) => (
                <FormItem className="flex items-start justify-between gap-2 space-y-0">
                  <FormLabel className={fieldLabelClass}>{t('SHOW_BANNER')}</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className={switchClass}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="designSettings.banner.imageUrl"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-3 space-y-0 border-b border-[#e7e7e7] pb-6 dark:border-border">
                  <FormLabel className={fieldLabelClass}>{t('IMAGE')}</FormLabel>
                  <FormControl>
                    {field.value ? (
                      <div className="flex items-start justify-between rounded-lg border border-[#e7e7e7] bg-white p-4 dark:border-border dark:bg-card">
                        <div className="relative h-20 max-h-20 flex-1 overflow-hidden rounded-sm">
                          <img
                            src={field.value}
                            alt={t('WIDGET_BANNER_ALT')}
                            className="h-full w-full object-contain"
                          />
                        </div>
                        <Button
                          variant="ghost"
                          className="h-fit w-fit p-0 text-[#3d3d3d] hover:bg-transparent dark:text-foreground"
                          type="button"
                          onClick={() => {
                            field.onChange('');
                            form.setValue('designSettings.banner.imageId', '');
                          }}
                        >
                          <Trash className="size-4" />
                        </Button>
                      </div>
                    ) : (
                      <div
                        className={cn(
                          'relative flex h-[171px] w-full cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-[#e7e7e7] bg-[#f6f6f6] transition-colors hover:bg-[#f0f0f0] dark:border-border dark:bg-muted/30 dark:hover:bg-muted/40',
                          isUploading && 'pointer-events-none opacity-50'
                        )}
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                      >
                        {!isUploading ? (
                          <div className="flex flex-col items-center justify-center gap-[18px]">
                            <CloudUpload className="size-10 text-[#3d3d3d] dark:text-foreground" />
                            <div className="flex flex-col items-center gap-[3px] text-center">
                              <p className="flex flex-wrap items-center justify-center gap-[9px] text-[18px] font-semibold leading-[1.5]">
                                <span className="text-[#3d3d3d] dark:text-foreground">
                                  {t('CLICK_TO_UPLOAD')}
                                </span>
                                <span className="text-[#888888] dark:text-muted-foreground">
                                  {t('OR_DRAG_AND_DROP')}
                                </span>
                              </p>
                              <p className="text-[16px] font-normal leading-[1.6] text-[#b0b0b0] dark:text-muted-foreground">
                                {t('MAX_IMAGE_SIZE_HINT')}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="text-[16px] text-[#888888] dark:text-muted-foreground">
                            {t('UPLOADING')}
                          </div>
                        )}
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileUpload(file);
                          }}
                        />
                      </div>
                    )}
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="designSettings.banner.text"
              render={({ field }) => (
                <FormItem className="flex flex-col gap-3 space-y-0">
                  <FormLabel className={fieldLabelClass}>{t('TEXT')}</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={5} className={textareaClass} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Greeting Text — Figma 3042:36205 */}
        <FormField
          control={form.control}
          name="greeting"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-3 space-y-0">
              <FormLabel className={sectionTitleClass}>{t('GREETING_TEXT')}</FormLabel>
              <FormControl>
                <Textarea {...field} rows={5} className={textareaClass} />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      <div className="border-t border-[#e7e7e7] pt-4 dark:border-border">
        <Button
          className="h-12 w-full rounded-[62px] text-base font-semibold"
          type="submit"
          disabled={!isValid}
        >
          {t('SAVE')}
        </Button>
      </div>
    </div>
  );
};
